import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { queryClient } from "lib/queryClient";
import { isAuthDisabled } from "./authConfig";

// Two kinds of role live here, and conflating them is what broke LPI once
// already (see LPI_ROLES below).
//
// Permission *levels* — viewer (reporting pages only), editor (viewer plus
// upload/delete files), super_admin (Loginex staff, cross-tenant, reaches
// everything). These are inclusive and stack.
//
// Entitlements — lpi. Held by the customers who bought that module. An
// entitlement is orthogonal to level: an editor does NOT get lpi by being an
// editor, and holding lpi says nothing about whether you may upload.
export type RoleType = "viewer" | "editor" | "super_admin" | "lpi";

export const ROLES: { [name: string]: RoleType } = {
  VIEWER: "viewer" as RoleType,
  EDITOR: "editor" as RoleType,
  SUPER_ADMIN: "super_admin" as RoleType,
  LPI: "lpi" as RoleType,
};

// Levels are inclusive: an editor may do anything a viewer may, and
// super_admin (Loginex staff, cross-tenant) may do anything at all. hasRole
// matches with .some() over an array, so gates take one of these sets rather
// than a single role.
export const VIEWER_ROLES: RoleType[] = [
  ROLES.VIEWER,
  ROLES.EDITOR,
  ROLES.SUPER_ADMIN,
];
export const EDITOR_ROLES: RoleType[] = [ROLES.EDITOR, ROLES.SUPER_ADMIN];

// The LPI module. Deliberately does NOT include viewer or editor: those are
// levels, and every logged-in user holds one, so listing them here would hand
// LPI to the whole tenant base. That is exactly the regression 4a61661
// introduced when it rewrote `roles: [ROLES.LPI]` to VIEWER_ROLES. super_admin
// is present because Loginex staff reach everything.
export const LPI_ROLES: RoleType[] = [ROLES.LPI, ROLES.SUPER_ADMIN];

/**
 * Roles from the login response, narrowed to the ones this app declares.
 * A key absent from ROLES has no effect here, so adding a role in Zitadel is
 * inert until it is declared above.
 */
export const filterKnownRoles = (roles?: string[]): RoleType[] =>
  roles?.filter((r): r is RoleType =>
    Object.values(ROLES).includes(r as RoleType)
  ) ?? [];

/**
 * Order-insensitive comparison of two role sets. A role re-check that returns
 * what we already hold must not replace the array: a fresh array identity
 * re-renders every hasRole() consumer in the tree on every navigation.
 */
const sameRoleSet = (a: RoleType[], b: RoleType[]): boolean =>
  a.length === b.length && a.every((role) => b.includes(role));

// Roles granted to the mock user when auth is disabled for local dev, so
// role-gated screens (e.g. the media admin portal) are reachable without a
// real Zitadel session. Only ever used when isAuthDisabled is true.
export const MOCK_DEV_ROLES: RoleType[] = [ROLES.EDITOR, ROLES.SUPER_ADMIN];

// Login error type
export type LoginErrorType =
  | "NEW_PASSWORD_REQUIRED"
  | "MFA_SMS_REQUIRED"
  | "LOGIN_ERROR";
export const LOGIN_ERRORS: { [name: string]: LoginErrorType } = {
  NEW_PASSWORD_REQUIRED: "NEW_PASSWORD_REQUIRED" as LoginErrorType,
  MFA_SMS_REQUIRED: "MFA_SMS_REQUIRED" as LoginErrorType,
  LOGIN_ERROR: "LOGIN_ERROR" as LoginErrorType,
};

type JwtPayload = Record<string, any>;

export type AuthSession = {
  getAccessToken: () => { getJwtToken: () => string; payload: JwtPayload };
  getIdToken: () => { getJwtToken: () => string; payload: JwtPayload };
  getRefreshToken: () => { getToken: () => string };
  isValid: () => boolean;
};

type AuthTokens = {
  sessionToken: string;
  sessionId: string;
  expiresAt?: number;
  roles?: string[];
  user?: {
    email?: string;
    givenName?: string;
    familyName?: string;
    organization?: string;
    location?: string;
    logoUrl?: string;
  };
};

const AUTH_STORAGE_KEY = "loginex.auth";

export interface ILoginErrorReturn {
  type: LoginErrorType;
  user?: any;
  message?: string;
}

export interface IUserInfo {
  givenName: string;
  familyName: string;
  organization: string;
  location: string;
  logoUrl: string;
  embedUrl: string;
  embedToken: string;
  reportId: string;
  exportReportId: string;
}

export type MfaFactorType = "totp" | "otp_sms" | "otp_email";

export type MfaRegistration = {
  uri: string;
  secret?: string;
};

interface ILoginState {
  isMfaStarted: boolean;
  cognitoUser?: any;
  phoneNr: string;
  sessionId?: string;
  sessionToken?: string;
  factor?: MfaFactorType;
  mfaRegistration?: MfaRegistration;
}

// Object structure for UserInfo lambda
export const defaultUserInfo: IUserInfo = {
  givenName: "",
  familyName: "",
  organization: "",
  location: "",
  logoUrl: "",
  embedUrl: "",
  embedToken: "",
  reportId: "",
  exportReportId: "",
};

const defaultLoginState: ILoginState = {
  isMfaStarted: false,
  cognitoUser: undefined,
  phoneNr: "",
  sessionId: undefined,
  sessionToken: undefined,
  factor: undefined,
  mfaRegistration: undefined,
};

const mockUserInfoData: IUserInfo = {
  givenName: "Mock",
  familyName: "User",
  organization: "Protium",
  location: "Local",
  logoUrl: "",
  embedUrl: "https://example.com/mock",
  embedToken: "mock-embed-token",
  reportId: "mock-report-id",
  exportReportId: "mock-export-report-id",
};

const buildSession = (tokens: AuthTokens): AuthSession => {
  const accessPayload = tokens.user
    ? {
        email: tokens.user.email,
        given_name: tokens.user.givenName,
        family_name: tokens.user.familyName,
        client_id: tokens.user.email,
        sessionId: tokens.sessionId,
      }
    : {};
  const idPayload = accessPayload;

  return {
    getAccessToken: () => ({
      getJwtToken: () => tokens.sessionToken,
      payload: accessPayload,
    }),
    getIdToken: () => ({
      getJwtToken: () => tokens.sessionToken,
      payload: idPayload,
    }),
    getRefreshToken: () => ({
      getToken: () => "",
    }),
    isValid: () =>
      tokens.expiresAt ? tokens.expiresAt * 1000 > Date.now() : true,
  };
};

const createMockSession = (): AuthSession =>
  buildSession({
    sessionToken: "mock-session-token",
    sessionId: "mock-session-id",
    expiresAt: Math.floor(Date.now() / 1000) + 3600,
    user: {
      email: "mock@loginex.local",
      givenName: "Mock",
      familyName: "User",
    },
  });

const getSessionRoles = (session: AuthSession): RoleType[] => {
  const { payload } = session.getAccessToken();
  const rolesClaim = payload["urn:zitadel:iam:org:project:roles"];
  if (rolesClaim && typeof rolesClaim === "object") {
    return Object.keys(rolesClaim as Record<string, unknown>) as RoleType[];
  }

  const groups = payload["cognito:groups"];
  if (!groups) {
    // No roles claim. A Zitadel session token is an opaque handle, not a JWT,
    // so this branch is reached by every session that was not handed roles
    // out-of-band by the login response. Report "we do not know", never
    // [viewer]: guessing viewer silently downgrades an editor (the delete
    // control just vanishes, with no error to explain it) and silently grants
    // read access to a user nobody has vouched for.
    return [];
  }

  return Array.isArray(groups) ? (groups as RoleType[]) : [groups as RoleType];
};

const mockSession = createMockSession();

const notSetUp = "Not set up";

class AuthError extends Error {
  type: LoginErrorType;

  constructor(message: string, type: LoginErrorType) {
    super(message);
    this.name = "AuthError";
    this.type = type;
  }
}

export interface IAccountContext {
  userInfo: IUserInfo;
  authenticate: (Username: string, Password: string) => Promise<void>;
  isAuthenticated: boolean;
  getSession: () => Promise<AuthSession>;
  cognitoSession?: AuthSession;
  hasRole: (role: RoleType | RoleType[]) => boolean;
  /**
   * Re-reads this session's roles from the backend and applies them. Never
   * rejects and never clears roles on failure — see the implementation.
   */
  refreshRoles: () => Promise<void>;
  sendCode: (Username: string) => Promise<any>;
  resetPassword: (
    code: string,
    Username: string,
    Password: string,
    userId?: string
  ) => Promise<string>;
  verifyMfaCode: (code: string) => Promise<void>;
  logout: () => void;
  needsPasswordReset: boolean;
  needsMfaSmsCode: boolean;
  setNeedsMfaSmsCode: Dispatch<SetStateAction<boolean>>;
  loginState: ILoginState;
  checkUserInfo: (session: AuthSession, resolve: any, reject: any) => void;
  setIsAuthenticated: Dispatch<SetStateAction<boolean>>;
}

/* eslint-disable @typescript-eslint/no-unused-vars */
const defaultContext: IAccountContext = {
  userInfo: defaultUserInfo,
  authenticate: (_Username, _Password) => Promise.reject<void>(notSetUp),
  isAuthenticated: false,
  getSession: () => Promise.reject<AuthSession>(notSetUp),
  hasRole: (_role) => false,
  refreshRoles: () => Promise.resolve(),
  sendCode: (_Username) => Promise.reject<any>(notSetUp),
  resetPassword: (_code, _Username, _Password, _userId) =>
    Promise.reject<string>(notSetUp),
  logout: () => {
    //
  },
  verifyMfaCode: (_code: string) => Promise.reject<void>(notSetUp),
  needsMfaSmsCode: false,
  needsPasswordReset: false,
  cognitoSession: undefined,
  loginState: defaultLoginState,
  checkUserInfo: () => {
    //
  },
  setIsAuthenticated: () => {
    //
  },
  setNeedsMfaSmsCode: () => {
    //
  },
};
/* eslint-enable @typescript-eslint/no-unused-vars */

const AccountContext: React.Context<IAccountContext> =
  createContext(defaultContext);

const AccountProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] =
    useState<boolean>(isAuthDisabled);
  const [cognitoSession, setCognitoSession] = useState<AuthSession | undefined>(
    isAuthDisabled ? mockSession : undefined
  );
  const [authTokens, setAuthTokens] = useState<AuthTokens | null>(() => {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      return stored ? (JSON.parse(stored) as AuthTokens) : null;
    } catch {
      return null;
    }
  });
  const [userInfo, setUserInfo] = useState<IUserInfo>(
    isAuthDisabled ? mockUserInfoData : defaultUserInfo
  );
  // Empty means "not known yet", which is what an unauthenticated provider
  // actually knows. [ROLES.VIEWER] here would hand viewer access to anyone
  // before a session is established.
  const [userRoles, setUserRoles] = useState<RoleType[]>(
    isAuthDisabled ? MOCK_DEV_ROLES : []
  );
  const [needsPasswordReset, setNeedsPasswordReset] = useState<boolean>(false);
  const [needsMfaSmsCode, setNeedsMfaSmsCode] = useState<boolean>(false);

  const [loginState, setLoginState] = useState<ILoginState>(defaultLoginState);

  const persistTokens = useCallback((tokens: AuthTokens | null) => {
    setAuthTokens(tokens);
    try {
      if (tokens) {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(tokens));
      } else {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const deriveUserInfo = useCallback(
    (tokens: AuthTokens, user?: Partial<IUserInfo> & { email?: string }) => {
      const payload = (tokens.user ?? {}) as Partial<IUserInfo> & {
        email?: string;
        given_name?: string;
        family_name?: string;
        organization?: string;
        logoUrl?: string;
        embedUrl?: string;
        embedToken?: string;
        exportReportId?: string;
      };
      setUserInfo({
        givenName:
          user?.givenName ??
          payload.givenName ??
          payload.given_name ??
          user?.email?.split("@")[0] ??
          "",
        familyName:
          user?.familyName ?? payload.familyName ?? payload.family_name ?? "",
        organization: payload.organization ?? "",
        location: payload.location ?? "",
        logoUrl: payload.logoUrl ?? user?.logoUrl ?? "",
        embedUrl: payload.embedUrl ?? user?.embedUrl ?? "",
        embedToken: payload.embedToken ?? user?.embedToken ?? "",
        reportId: payload.reportId ?? user?.reportId ?? "",
        exportReportId: payload.exportReportId ?? user?.exportReportId ?? "",
      });
    },
    []
  );

  const buildAndStoreSession = useCallback(
    (tokens: AuthTokens, user?: Partial<IUserInfo> & { email?: string }) => {
      const session = buildSession(tokens);
      setCognitoSession(session);
      const roleList = tokens.roles
        ? filterKnownRoles(tokens.roles)
        : getSessionRoles(session);
      setUserRoles(roleList);
      setIsAuthenticated(true);
      deriveUserInfo(tokens, user);
      persistTokens(tokens);
      setNeedsMfaSmsCode(false);
      setNeedsPasswordReset(false);
      return session;
    },
    [
      deriveUserInfo,
      persistTokens,
      setCognitoSession,
      setIsAuthenticated,
      setNeedsMfaSmsCode,
      setNeedsPasswordReset,
      setUserRoles,
    ]
  );

  /*
   * Destroys session and un-authenticates user
   */
  const logout = useCallback(() => {
    // Wipe the react-query cache so the next login (possibly a different client)
    // never sees the previous session's cached data. The QueryClient is a
    // persistent singleton with staleTime: Infinity, so without this the
    // Overview dashboard would serve stale cross-tenant data on client switch.
    queryClient.clear();

    if (isAuthDisabled) {
      setIsAuthenticated(false);
      setCognitoSession(undefined);
      setUserInfo(defaultUserInfo);
      setUserRoles(MOCK_DEV_ROLES);
      setLoginState(defaultLoginState);
      setNeedsMfaSmsCode(false);
      persistTokens(null);
      return;
    }

    setIsAuthenticated(false);
    setCognitoSession(undefined);
    setUserInfo(defaultUserInfo);
    // Roles belong to the session that just ended. Leaving them in place lets
    // the next thing that asks hasRole() get the departed user's answer.
    setUserRoles([]);
    setLoginState(defaultLoginState);
    setNeedsMfaSmsCode(false);
    persistTokens(null);
  }, [persistTokens]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401 && !isAuthDisabled) {
          const requestUrl: string = error?.config?.url ?? "";
          const isAuthEndpoint =
            requestUrl.includes("/auth/mfa") ||
            requestUrl.includes("/auth/login");
          if (!isAuthEndpoint) {
            logout();
            window.location.href = "/login";
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  /*
   * Refreshes session using stored tokens or refresh token
   */
  const refreshTokens = useCallback(async (): Promise<AuthSession> => {
    // No refresh flow for session tokens; rely on session validity.
    if (!authTokens) {
      logout();
      return Promise.reject(new Error("No session available"));
    }
    return buildAndStoreSession(authTokens);
  }, [authTokens, buildAndStoreSession, logout]);

  /*
   * Returns current session, attempting refresh if needed
   */
  const getSession: () => Promise<AuthSession> = useCallback(async () => {
    if (isAuthDisabled) {
      setIsAuthenticated(true);
      setCognitoSession(mockSession);
      setUserRoles(MOCK_DEV_ROLES);
      setUserInfo(mockUserInfoData);
      return Promise.resolve(mockSession);
    }

    if (cognitoSession && cognitoSession.isValid()) {
      return cognitoSession;
    }

    if (authTokens) {
      const isExpired =
        authTokens.expiresAt !== undefined &&
        authTokens.expiresAt * 1000 <= Date.now();
      if (isExpired) {
        return refreshTokens();
      }
      return buildAndStoreSession(authTokens);
    }

    logout();
    return Promise.reject(new Error("User disconnected"));
  }, [authTokens, buildAndStoreSession, cognitoSession, logout, refreshTokens]);

  /*
   * Re-reads the roles Zitadel currently grants this session and applies them,
   * so an administrator demoting someone (editor -> viewer) takes effect
   * without waiting for a logout. Roles were previously captured once at login
   * and rehydrated from localStorage forever, which left demoted users looking
   * at editor controls that the backend then 403s.
   *
   * Deliberately total: it resolves on every path and throws nothing, so a
   * caller can fire it and forget it.
   *
   * The failure policy is the whole point. A network blip, a timeout, a 500,
   * or a body that is not the shape we expect leaves the roles already in
   * state exactly as they are. Clearing them would fail every role gate and
   * bounce a perfectly valid user to /no-access — far worse than roles that
   * are a few seconds stale. Only a response we successfully read is applied,
   * and an empty array in such a response IS applied: that is a real demotion
   * to no roles at all, and landing on /no-access is then correct.
   */
  const refreshRoles = useCallback(async (): Promise<void> => {
    // Auth-disabled local dev runs on MOCK_DEV_ROLES and has no backend to
    // ask; calling out would 404 and, worse, could unset the mock roles.
    if (isAuthDisabled) return;
    // Nothing to re-check before a session exists. The login and reset screens
    // render with isAuthenticated false and must stay silent.
    if (!isAuthenticated) return;
    // getSession() logs out and rejects when there are no stored tokens, so
    // check first rather than letting a role re-check end the session.
    if (!authTokens) return;

    try {
      const session = await getSession();
      const jwtToken = session?.getAccessToken?.()?.getJwtToken?.();
      const sessionId = session?.getIdToken?.()?.payload?.sessionId;
      if (!jwtToken || !sessionId) return;

      const apiBase =
        process.env.REACT_APP_LPI_API_URL || process.env.REACT_APP_DOMAIN;

      const response = await axios.get(`${apiBase}/auth/me`, {
        headers: {
          Authorization: `Session ${jwtToken}`,
          "X-Session-Id": sessionId,
        },
      });

      const roles = response?.data?.roles;
      // The contract says roles is always an array. If it is not, we did not
      // get an answer we understand — treat it as a failure and keep what we
      // have rather than reading roles out of garbage.
      if (!Array.isArray(roles)) return;

      // Same narrowing as the login path: a role Zitadel knows but this app
      // does not is inert, never a grant.
      const nextRoles = filterKnownRoles(roles as string[]);

      setUserRoles((current) =>
        sameRoleSet(current, nextRoles) ? current : nextRoles
      );

      // Keep the persisted blob in step, or a page reload would rehydrate the
      // roles this call just superseded. Only written when they actually
      // changed, so the common no-op navigation touches no state at all.
      if (!sameRoleSet(filterKnownRoles(authTokens.roles), nextRoles)) {
        persistTokens({ ...authTokens, roles: nextRoles });
      }
    } catch {
      // Keep the roles already in state. See the comment above.
    }
  }, [authTokens, getSession, isAuthenticated, persistTokens]);

  /*
   * Login form.
   * Authenticates username with backend (Zitadel credential exchange)
   */
  const authenticate: (
    Username: string,
    Password: string
  ) => Promise<void> = useCallback(
    async (Username: string, Password: string) => {
      if (isAuthDisabled) {
        setNeedsMfaSmsCode(false);
        setNeedsPasswordReset(false);
        setUserInfo(mockUserInfoData);
        setCognitoSession(mockSession);
        // MOCK_DEV_ROLES, not getSessionRoles(mockSession): the mock token
        // carries no roles claim, so deriving from it falls back to [viewer]
        // and local dev with auth disabled bounces off the editor pages. The
        // other auth-disabled paths (initial state, getSession) already use
        // MOCK_DEV_ROLES; this one was the odd one out.
        setUserRoles(MOCK_DEV_ROLES);
        setIsAuthenticated(true);
        return;
      }

      const apiBase =
        process.env.REACT_APP_LPI_API_URL || process.env.REACT_APP_DOMAIN;

      try {
        const response = await axios.post(`${apiBase}/auth/login`, {
          username: Username,
          password: Password,
        });

      if (response.data.mfa) {
        const mfaPayload = response.data.mfa;
        setNeedsMfaSmsCode(true);
        setLoginState({
          isMfaStarted: true,
          cognitoUser: undefined,
          phoneNr: mfaPayload.hint ?? "",
          sessionId: mfaPayload.sessionId,
          sessionToken: mfaPayload.sessionToken,
          factor: mfaPayload.type,
          mfaRegistration: mfaPayload.registration,
        });
        return;
      }

        const tokens: AuthTokens = {
          sessionToken: response.data.sessionToken,
          sessionId: response.data.sessionId,
          expiresAt:
            response.data.expiresAt ??
            Math.floor(Date.now() / 1000) + 60 * 60 * 8, // fallback 8h
          roles: response.data.roles ?? response.data.user?.roles,
          user: response.data.user,
        };

        buildAndStoreSession(tokens, response.data.user);
      } catch (error: any) {
        logout();
        const message =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Login failed";
        throw new AuthError(message, LOGIN_ERRORS.LOGIN_ERROR);
      }
    },
    [buildAndStoreSession, logout]
  );

  const verifyMfaCode = useCallback(
    async (code: string) => {
      if (isAuthDisabled) {
        return;
      }

      const { sessionId, sessionToken, factor } = loginState;
      if (!sessionId || !sessionToken || !factor) {
        throw new AuthError("MFA session is missing", LOGIN_ERRORS.LOGIN_ERROR);
      }

      const isSetupMode = Boolean(loginState.mfaRegistration?.uri);

      const apiBase =
        process.env.REACT_APP_LPI_API_URL || process.env.REACT_APP_DOMAIN;

      try {
        const response = await axios.post(`${apiBase}/auth/mfa`, {
          sessionId,
          sessionToken,
          code,
          type: factor,
          setup: isSetupMode,
        });

        const tokens: AuthTokens = {
          sessionToken: response.data.sessionToken,
          sessionId: response.data.sessionId,
          expiresAt:
            response.data.expiresAt ??
            Math.floor(Date.now() / 1000) + 60 * 60 * 8,
          roles: response.data.roles ?? response.data.user?.roles,
          user: response.data.user,
        };

        setLoginState(defaultLoginState);
        buildAndStoreSession(tokens, response.data.user);
      } catch (error: any) {
        const message =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Login failed";
        throw new AuthError(message, LOGIN_ERRORS.LOGIN_ERROR);
      }
    },
    [buildAndStoreSession, loginState]
  );

  /*
   * Checks if user has any of the specified roles
   */
  const hasRole: (role: RoleType | RoleType[]) => boolean = useCallback(
    (roles) => {
      const roleList = Array.isArray(roles) ? roles : [roles];

      return !roleList.length || roleList.some((i) => userRoles.includes(i));
    },
    [userRoles]
  );

  /*
   * Sets session values to context, authenticates user
   */
  const login = useCallback((session: AuthSession) => {
    setCognitoSession(session);
    setUserRoles(getSessionRoles(session));

    /* Use this for testing user roles: setUserRoles([ROLES.VIEWER]); */

    if (!loginState.isMfaStarted) {
      setIsAuthenticated(true);
    }
  }, [loginState.isMfaStarted]);

  const checkUserInfo = useCallback(
    (session: AuthSession, resolve: any, reject: any) => {
      try {
        login(session);
        resolve(session);
      } catch (error) {
        reject(error);
      }
    },
    [login]
  );

  /* Password reset via Zitadel (backend proxies to the Zitadel API) */
  const sendCode: (Username: string) => Promise<any> = useCallback(
    async (Username: string) => {
      const apiBase =
        process.env.REACT_APP_LPI_API_URL || process.env.REACT_APP_DOMAIN;

      try {
        await axios.post(`${apiBase}/auth/forgot-password`, {
          username: Username,
        });
      } catch (error: any) {
        const message =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to send reset code";
        throw message;
      }
    },
    []
  );

  const resetPassword: (
    code: string,
    Username: string,
    Password: string,
    userId?: string
  ) => Promise<string> = useCallback(
    async (
      code: string,
      Username: string,
      Password: string,
      userId?: string
    ) => {
      const apiBase =
        process.env.REACT_APP_LPI_API_URL || process.env.REACT_APP_DOMAIN;

      try {
        await axios.post(`${apiBase}/auth/reset-password`, {
          ...(userId ? { userId } : { username: Username }),
          code,
          newPassword: Password,
        });
        return "success";
      } catch (error: any) {
        const message =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to reset password";
        throw message;
      }
    },
    []
  );

  const providerValue = useMemo(
    () => ({
      authenticate,
      isAuthenticated,
      getSession,
      cognitoSession,
      userInfo,
      hasRole,
      refreshRoles,
      sendCode,
      resetPassword,
      logout,
      needsPasswordReset,
      needsMfaSmsCode,
      loginState,
      checkUserInfo,
      setIsAuthenticated,
      setNeedsMfaSmsCode,
      verifyMfaCode,
    }),
    [
      authenticate,
      isAuthenticated,
      getSession,
      cognitoSession,
      userInfo,
      hasRole,
      refreshRoles,
      sendCode,
      resetPassword,
      logout,
      needsPasswordReset,
      needsMfaSmsCode,
      loginState,
      checkUserInfo,
      setIsAuthenticated,
      setNeedsMfaSmsCode,
      verifyMfaCode,
    ]
  );

  return (
    <AccountContext.Provider value={providerValue}>
      {children}
    </AccountContext.Provider>
  );
};

export { AccountProvider, AccountContext };
